import assemblyBindings from './bindings.json';
import { resolveLayerFromRegistry } from './resolver';
import type {
  CameraLayer,
  HandlingLayer,
  HydratedSceneAssembly,
  LightingLayer,
  ParticlesLayer,
  PostFxLayer,
  RacerLayer,
  RacerMechanicsLayer,
  RacerPaintLayer,
  RacerRigFxLayer,
  RacerSoftBodyLayer,
  SceneAssembly,
  SceneLayerKind,
  SceneLayerRecord,
  SceneLayerRegistry,
  TrackLayer,
} from './types';

const assemblyModules = import.meta.glob<SceneAssembly>('./assemblies/*.json', {
  eager: true,
  import: 'default',
});

const layerModules = import.meta.glob<SceneLayerRecord>('./templates/*/*.json', {
  eager: true,
  import: 'default',
});

const ASSEMBLIES = buildAssemblyRegistry();
const LAYERS = buildLayerRegistry();

export const SCENE_ASSEMBLY_BINDINGS: Record<string, string> = assemblyBindings as Record<string, string>;

export function loadAssemblyForTrack(trackUrl: string): HydratedSceneAssembly {
  const id = SCENE_ASSEMBLY_BINDINGS[trackUrl] ?? trackUrlToAssemblyId(trackUrl);
  return loadAssembly(id);
}

export function loadAssembly(id: string): HydratedSceneAssembly {
  const assembly = ASSEMBLIES[id];
  if (!assembly) throw new Error(`Missing scene assembly: ${id}`);

  const racer = resolveLayerFromRegistry(LAYERS, 'racer', assembly.racer) as unknown as RacerLayer;
  return {
    ...deepClone(assembly),
    layers: {
      track: resolveLayerFromRegistry(LAYERS, 'track', assembly.track) as unknown as TrackLayer,
      lighting: resolveLayerFromRegistry(LAYERS, 'lighting', assembly.lighting) as unknown as LightingLayer,
      postFx: resolveLayerFromRegistry(LAYERS, 'postfx', assembly.postFx) as unknown as PostFxLayer,
      particles: resolveLayerFromRegistry(LAYERS, 'particles', assembly.particles) as unknown as ParticlesLayer,
      camera: resolveLayerFromRegistry(LAYERS, 'camera', assembly.camera) as unknown as CameraLayer,
      handling: resolveLayerFromRegistry(LAYERS, 'handling', assembly.handling) as unknown as HandlingLayer,
      racer,
      racerMechanics: resolveLayerFromRegistry(LAYERS, 'racer-mechanics', racer.mechanics) as unknown as RacerMechanicsLayer,
      racerSoftBody: resolveLayerFromRegistry(LAYERS, 'racer-softbody', racer.softBody) as unknown as RacerSoftBodyLayer,
      racerRigFx: resolveLayerFromRegistry(LAYERS, 'racer-rigfx', racer.rigFx) as unknown as RacerRigFxLayer,
      racerPaint: resolveLayerFromRegistry(LAYERS, 'racer-paint', racer.paint) as unknown as RacerPaintLayer,
    },
  };
}

export function listAssemblyIds(): string[] {
  return Object.keys(ASSEMBLIES).sort();
}

function buildAssemblyRegistry(): Record<string, SceneAssembly> {
  const out: Record<string, SceneAssembly> = {};
  for (const assembly of Object.values(assemblyModules)) out[assembly.id] = assembly;
  return out;
}

function buildLayerRegistry(): SceneLayerRegistry {
  const out: SceneLayerRegistry = {};
  for (const [path, layer] of Object.entries(layerModules)) {
    const kind = path.match(/templates\/([^/]+)\//)?.[1] as SceneLayerKind | undefined;
    if (!kind) continue;
    out[kind] ??= {};
    out[kind][layer.id] = layer;
  }
  return out;
}

function trackUrlToAssemblyId(trackUrl: string): string {
  return trackUrl
    .split('/')
    .pop()
    ?.replace(/\.csv$/, '')
    .replace(/-arcade$/, '') ?? trackUrl;
}

function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}
